/**
 * AI Hero Search - Ryzo Demo Store
 * Minimal search with integrated image search trigger + Onboarding
 */

(function() {
  'use strict';

  const searchForm = document.getElementById('ai-search-form');
  const searchInput = document.getElementById('ai-search-input');
  const searchBar = document.querySelector('.ai-hero__search-bar');
  const cameraBtn = document.getElementById('ai-camera-btn');
  const suggestions = document.querySelectorAll('.ai-hero__suggestion');
  function init() {
    setupTextSearch();
    setupCameraButton();
    setupSuggestions();
    setupPlaceholderAnimation();
    setupImageSearchTooltip();
  }

  // Text Search
  function setupTextSearch() {
    if (!searchForm) return;
    
    searchForm.addEventListener('submit', (e) => {
      const query = searchInput.value.trim();
      if (!query) {
        e.preventDefault();
        searchBar.classList.add('shake');
        setTimeout(() => searchBar.classList.remove('shake'), 400);
        searchInput.focus();
        return;
      }
      searchBar.classList.add('loading');
    });
  }

  // Camera Button - Directly triggers Ryzo's image search
  function setupCameraButton() {
    if (!cameraBtn) return;
    
    cameraBtn.addEventListener('click', (e) => {
      e.preventDefault();
      triggerRyzoImageSearch();
    });
  }

  function triggerRyzoImageSearch() {
    // Try multiple selectors for Ryzo's image search button
    const ryzoBtn = document.getElementById('float-image-icon') ||
                   document.querySelector('.ryzo-image-search-button') ||
                   document.querySelector('[data-file-input-id]') ||
                   document.getElementById('searchbar-image-search');
    
    if (ryzoBtn) {
      ryzoBtn.click();
    } else {
      // If button not found, try to find and click the file input directly
      const fileInput = document.querySelector('input[id^="ryzo-hidden-file-input"]');
      if (fileInput) {
        fileInput.click();
      } else {
        // Retry after a short delay (Ryzo might not be loaded yet)
        setTimeout(() => {
          const retryBtn = document.getElementById('float-image-icon') ||
                          document.querySelector('.ryzo-image-search-button');
          if (retryBtn) {
            retryBtn.click();
          }
        }, 500);
      }
    }
  }

  // Suggestions
  function setupSuggestions() {
    suggestions.forEach(btn => {
      btn.addEventListener('click', () => {
        const text = btn.dataset.suggestion;
        if (text) {
          typeText(text, () => {
            searchForm.submit();
          });
        }
      });
    });
  }

  function typeText(text, callback) {
    searchInput.value = '';
    searchInput.focus();
    let i = 0;
    
    function type() {
      if (i < text.length) {
        searchInput.value += text.charAt(i);
        i++;
        setTimeout(type, 25);
      } else if (callback) {
        setTimeout(callback, 200);
      }
    }
    type();
  }

  // Check if mobile
  function isMobile() {
    return window.innerWidth <= 640;
  }

  // Placeholder Animation
  function setupPlaceholderAnimation() {
    if (!searchInput) return;
    
    const allPlaceholders = [
      "a costume for a pirate themed party...",
      "cozy winter sweater with patterns...",
      "minimalist gold earrings...",
      "vintage leather messenger bag...",
      "comfortable running shoes..."
    ];
    
    // On mobile, exclude the first (pirate) placeholder
    const mobilePlaceholders = allPlaceholders.slice(1);
    
    function getPlaceholders() {
      return isMobile() ? mobilePlaceholders : allPlaceholders;
    }
    
    let index = 0;
    let charIndex = 0;
    let isDeleting = false;
    let isPaused = false;
    
    function animate() {
      if (document.activeElement === searchInput || searchInput.value) {
        setTimeout(animate, 100);
        return;
      }
      
      const placeholders = getPlaceholders();
      // Reset index if it's out of bounds (e.g., when switching between mobile/desktop)
      if (index >= placeholders.length) index = 0;
      
      const current = placeholders[index];
      
      if (isPaused) {
        isPaused = false;
        isDeleting = true;
        setTimeout(animate, 50);
        return;
      }
      
      if (isDeleting) {
        searchInput.placeholder = current.substring(0, charIndex - 1);
        charIndex--;
        
        if (charIndex === 0) {
          isDeleting = false;
          index = (index + 1) % placeholders.length;
        }
        setTimeout(animate, 30);
      } else {
        searchInput.placeholder = current.substring(0, charIndex + 1);
        charIndex++;
        
        if (charIndex === current.length) {
          isPaused = true;
          setTimeout(animate, 2000);
          return;
        }
        setTimeout(animate, 60);
      }
    }
    
    setTimeout(animate, 1500);
    
    searchInput.addEventListener('focus', () => {
      const placeholders = getPlaceholders();
      searchInput.placeholder = placeholders[0];
      
      // On mobile, scroll search bar into view when keyboard opens
      if (isMobile()) {
        setTimeout(() => {
          searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    });
  }

  // Stream typing effect (like GPT)
  function streamText(element, text, speed = 50) {
    return new Promise((resolve) => {
      element.innerHTML = '';
      let displayText = ''; // Buffer to build up the visible text
      let i = 0;
      
      function type() {
        if (i < text.length) {
          // Handle HTML tags - add them instantly to buffer
          if (text[i] === '<') {
            const tagEnd = text.indexOf('>', i);
            if (tagEnd !== -1) {
              displayText += text.substring(i, tagEnd + 1);
              i = tagEnd + 1;
              // Set full innerHTML each time to properly parse HTML
              element.innerHTML = displayText;
              setTimeout(type, 0);
              return;
            }
          }
          displayText += text[i];
          i++;
          // Set full innerHTML each time to properly parse HTML
          element.innerHTML = displayText;
          setTimeout(type, speed);
        } else {
          resolve();
        }
      }
      type();
    });
  }

  // Onboarding tooltips - sequential with stream typing
  function setupImageSearchTooltip() {
    const cameraTip = document.getElementById('image-search-tooltip');
    const cameraTipText = document.getElementById('tooltip-text');
    const searchbarTip = document.getElementById('searchbar-tooltip');
    const searchbarTipText = searchbarTip?.querySelector('.ai-hero__bar-tip-text');
    
    if (!cameraTip || !cameraTipText) return;
    
    // Check if shown 10 times already (using localStorage for persistence across sessions)
    const viewCount = parseInt(localStorage.getItem('ryzo_tip_view_count') || '0', 10);
    if (viewCount >= 10) return;
    
    // Increment view count
    localStorage.setItem('ryzo_tip_view_count', String(viewCount + 1));
    
    const cameraMessage = 'add image search to your store · <a href="https://apps.shopify.com/ryzo-search" target="_blank">install free</a>';
    const searchbarMessage = 'AI search that converts · <a href="https://apps.shopify.com/ryzo-search" target="_blank">add to store</a>';
    
    // Phase 1: Camera button tooltip (after 5 seconds)
    setTimeout(async () => {
      cameraTip.classList.add('visible');
      cameraBtn.classList.add('glow');
      
      // Stream type the camera message (slower - 45ms per char)
      await streamText(cameraTipText, cameraMessage, 45);
      
      // Hide camera tip after 5 more seconds (longer display time)
      setTimeout(() => {
        cameraTip.style.opacity = '0';
        cameraTip.style.transform = 'translateY(4px)';
        cameraBtn.classList.remove('glow');
        
        setTimeout(async () => {
          cameraTip.classList.remove('visible');
          
          // Phase 2: Search bar tooltip (5 second delay)
          if (searchbarTip && searchbarTipText) {
            setTimeout(async () => {
              searchbarTipText.innerHTML = '';
              searchbarTip.classList.add('visible');
              searchBar.classList.add('glow');
              
              // Stream type the searchbar message (slower - 45ms per char)
              await streamText(searchbarTipText, searchbarMessage, 45);
              
              // Hide searchbar tip after 5 more seconds (longer display time)
              setTimeout(() => {
                searchbarTip.style.opacity = '0';
                searchbarTip.style.transform = 'translateY(4px)';
                searchBar.classList.remove('glow');
                setTimeout(() => searchbarTip.classList.remove('visible'), 300);
              }, 5000);
              
            }, 5000);
          }
        }, 300);
        
      }, 5000);
      
    }, 5000);
    
    // Hide on camera button click
    cameraBtn.addEventListener('click', () => {
      if (cameraTip.classList.contains('visible')) {
        cameraTip.style.opacity = '0';
        cameraBtn.classList.remove('glow');
        setTimeout(() => cameraTip.classList.remove('visible'), 300);
      }
    });
    
    // Hide searchbar tip on input focus
    searchInput.addEventListener('focus', () => {
      if (searchbarTip && searchbarTip.classList.contains('visible')) {
        searchbarTip.style.opacity = '0';
        searchBar.classList.remove('glow');
        setTimeout(() => searchbarTip.classList.remove('visible'), 300);
      }
    });
  }

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
